import { fabric } from 'fabric';
import type { CanvasEx } from '@/typings/canvas';
import type { IEvent, Object as FabricObject, Point } from 'fabric/fabric-impl';

type GuideLine = Record<'x1' | 'x2' | 'y1' | 'y2', number>;

interface FabricObjectMeasures {
    centerPoint: Point;
    halfWidth: number;
    halfHeight: number;
    edge?: number;
}

/**
 *
 * Lines default parameters
 */
const lineDefaultStyles = {
    offset: 5,
    margin: 4,
    width: 1,
    color: '#E863F4'
};

export class FabricAlignmentGuidelines {
    private context: CanvasRenderingContext2D;
    private viewportTransform: number[] = [];

    private objectVerticalLines: GuideLine[] = [];
    private objectHorizontalLines: GuideLine[] = [];

    private canvasMeasures = { width: 0, height: 0, centerWidth: 0, centerHeight: 0 };
    private canvasWidthCenterMap: Set<number> = new Set();
    private canvasHeightCenterMap: Set<number> = new Set();
    private isInVerticalCenter = false;
    private isInHorizontalCenter = false;
    private shiftKeyPressed = false;

    constructor(public canvas: CanvasEx, private lineStyle = lineDefaultStyles) {
        this.context = canvas.getSelectionContext();

        this.setCanvasMeasures();

        this.mouseDown = this.mouseDown.bind(this);
        this.mouseUp = this.mouseUp.bind(this);
        this.beforeRender = this.beforeRender.bind(this);
        this.objectMoving = this.objectMoving.bind(this);
        this.afterRender = this.afterRender.bind(this);
    }

    setCanvasMeasures(canvasWidth?: number, canvasHeight?: number) {
        if (!canvasHeight || !canvasWidth) {
            const center = this.canvas.getCenter();
            this.canvasMeasures = {
                width: center.left * 2,
                height: center.top * 2,
                centerWidth: center.left,
                centerHeight: center.top
            };
        } else {
            this.canvasMeasures = {
                width: canvasWidth,
                height: canvasHeight,
                centerWidth: canvasWidth / 2,
                centerHeight: canvasHeight / 2
            };
        }

        this.fillCenterMap(this.canvasWidthCenterMap, this.canvasMeasures.centerWidth, this.lineStyle.margin);
        this.fillCenterMap(this.canvasHeightCenterMap, this.canvasMeasures.centerHeight, this.lineStyle.margin);
    }

    private fillCenterMap = (centerMap: Set<number>, center: number, margin: number) => {
        centerMap.clear();
        for (let i = center - margin, len = center + margin; i <= len; i++) {
            centerMap.add(Math.round(i));
        }
    };

    init() {
        this.canvas.on('mouse:down', this.mouseDown);
        this.canvas.on('mouse:up', this.mouseUp);
        this.canvas.on('before:render', this.beforeRender);
        this.canvas.on('object:moving', this.objectMoving);
        this.canvas.on('after:render', this.afterRender);

        window.addEventListener('customily-shift-pressed', () => {
            this.shiftKeyPressed = true;
        });

        window.addEventListener('customily-shift-released', () => {
            this.shiftKeyPressed = false;
        });
    }

    private mouseDown() {
        this.viewportTransform = this.canvas.viewportTransform || [];
    }

    private mouseUp() {
        this.isInHorizontalCenter = this.isInVerticalCenter = false;

        this.objectVerticalLines = [];
        this.objectHorizontalLines = [];

        this.canvas.renderAll();
    }

    private beforeRender() {
        if (this.canvas.contextTop) {
            this.canvas.clearContext(this.canvas.contextTop);
        }
        this.context.clearRect(0, 0, this.canvasMeasures.width, this.canvasMeasures.height);
    }

    private objectMoving(event: IEvent) {
        const activeObject = event.target;
        if (!event.transform || !activeObject) return;

        this.objectHorizontalLines = [];
        this.objectVerticalLines = [];

        const activeObjectCenter = activeObject.getCenterPoint();

        //#region Object lines
        const activeObjectBoundingRect = activeObject.getBoundingRect();
        const activeObjectHalfHeight = activeObjectBoundingRect.height / (2 * this.viewportTransform[3]);
        const activeObjectHalfWidth = activeObjectBoundingRect.width / (2 * this.viewportTransform[0]);

        const activeObjectMeasures = {
            centerPoint: activeObjectCenter,
            halfHeight: activeObjectHalfHeight,
            halfWidth: activeObjectHalfWidth
        };

        const objects = this.canvas.getObjects();

        for (const object of objects) {
            if (object == activeObject || !object.visible)  {
                continue;
            }

            const objectCenter = object.getCenterPoint();
            const objectBoundingRect = object.getBoundingRect();
            const objectHalfHeight = objectBoundingRect.height / (2 * this.viewportTransform[3]);
            const objectHalfWidth = objectBoundingRect.width / (2 * this.viewportTransform[0]);

            const objectMeasures = {
                centerPoint: objectCenter,
                halfHeight: objectHalfHeight,
                halfWidth: objectHalfWidth
            };

            // snap by the horizontal center line
            this.snapVertical(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.x },
                { ...objectMeasures, edge: objectCenter.x },
                objectCenter.x
            );

            // snap by the left object edge matching left active edge
            this.snapVertical(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.x - activeObjectHalfWidth },
                { ...objectMeasures, edge: objectCenter.x - objectHalfWidth },
                objectCenter.x - objectHalfWidth + activeObjectHalfWidth
            );

            // snap by the left object edge matching right active edge
            this.snapVertical(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.x + activeObjectHalfWidth },
                { ...objectMeasures, edge: objectCenter.x - objectHalfWidth },
                objectCenter.x - objectHalfWidth - activeObjectHalfWidth
            );

            // snap by the right object edge matching right active edge
            this.snapVertical(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.x + activeObjectHalfWidth },
                { ...objectMeasures, edge: objectCenter.x + objectHalfWidth },
                objectCenter.x + objectHalfWidth - activeObjectHalfWidth
            );

            // snap by the right object edge matching left active edge
            this.snapVertical(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.x - activeObjectHalfWidth },
                { ...objectMeasures, edge: objectCenter.x + objectHalfWidth },
                objectCenter.x + objectHalfWidth + activeObjectHalfWidth
            );

            // snap by the vertical center line
            this.snapHorizontal(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.y },
                { ...objectMeasures, edge: objectCenter.y },
                objectCenter.y
            );

            // snap by the top object edge matching the top active edge
            this.snapHorizontal(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.y - activeObjectHalfHeight },
                { ...objectMeasures, edge: objectCenter.y - objectHalfHeight },
                objectCenter.y - objectHalfHeight + activeObjectHalfHeight
            );

            // snap by the top object edge matching the bottom active edge
            this.snapHorizontal(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.y + activeObjectHalfHeight },
                { ...objectMeasures, edge: objectCenter.y - objectHalfHeight },
                objectCenter.y - objectHalfHeight - activeObjectHalfHeight
            );

            // snap by the bottom object edge matching the bottom active edge
            this.snapHorizontal(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.y + activeObjectHalfHeight },
                { ...objectMeasures, edge: objectCenter.y + objectHalfHeight },
                objectCenter.y + objectHalfHeight - activeObjectHalfHeight
            );

            // snap by the bottom object edge matching the top active edge
            this.snapHorizontal(
                activeObject,
                { ...activeObjectMeasures, edge: activeObjectCenter.y - activeObjectHalfHeight },
                { ...objectMeasures, edge: objectCenter.y + objectHalfHeight },
                objectCenter.y + objectHalfHeight + activeObjectHalfHeight
            );
        }

        if (this.shiftKeyPressed) {
            this.movementGuides(activeObjectMeasures);
        }

        //#endregion

        // #region Canvas lines

        this.isInVerticalCenter = this.canvasWidthCenterMap.has(Math.round(activeObjectCenter.x));
        this.isInHorizontalCenter = this.canvasHeightCenterMap.has(Math.round(activeObjectCenter.y));

        if (this.isInVerticalCenter) {
            this.setObjectPosition(activeObject, this.canvasMeasures.centerWidth, activeObjectCenter.y);
        }

        if (this.isInHorizontalCenter) {
            this.setObjectPosition(activeObject, activeObjectCenter.x, this.canvasMeasures.centerHeight);
        }
        // #endregion
    }

    private snapHorizontal(
        activeObject: FabricObject,
        activeObjectMeasure: Omit<FabricObjectMeasures, 'halfHeight'>,
        objectMeasure: Omit<FabricObjectMeasures, 'halfHeight'>,
        snapCenter: number
    ) {
        const { edge: objEdge, centerPoint: objectCenter, halfWidth: objectHalfWidth } = objectMeasure;
        const {
            edge: activeObjEdge,
            centerPoint: activeObjectCenter,
            halfWidth: activeObjectHalfWidth
        } = activeObjectMeasure;

        if (!this.isInRange(objEdge!, activeObjEdge!)) return;
        this.objectHorizontalLines.push({
            y1: objEdge!,
            y2: objEdge!,
            x1:
                objectCenter.x < activeObjectCenter.x
                    ? objectCenter.x - objectHalfWidth - this.lineStyle.offset
                    : objectCenter.x + objectHalfWidth + this.lineStyle.offset,
            x2:
                activeObjectCenter.x > objectCenter.x
                    ? activeObjectCenter.x + activeObjectHalfWidth + this.lineStyle.offset
                    : activeObjectCenter.x - activeObjectHalfWidth - this.lineStyle.offset
        });
        this.setObjectPosition(activeObject, activeObjectCenter.x, snapCenter);
    }

    private snapVertical(
        activeObject: FabricObject,
        activeObjectMeasure: Omit<FabricObjectMeasures, 'halfWidth'>,
        objectMeasure: Omit<FabricObjectMeasures, 'halfWidth'>,
        snapCenter: number
    ) {
        const { edge: objEdge, centerPoint: objectCenter, halfHeight: objectHalfHeight } = objectMeasure;
        const {
            edge: activeObjEdge,
            centerPoint: activeObjectCenter,
            halfHeight: activeObjectHalfHeight
        } = activeObjectMeasure;
        if (!this.isInRange(objEdge!, activeObjEdge!)) return;
        this.objectVerticalLines.push({
            x1: objEdge!,
            x2: objEdge!,
            y1:
                objectCenter.y < activeObjectCenter.y
                    ? objectCenter.y - objectHalfHeight - this.lineStyle.offset
                    : objectCenter.y + objectHalfHeight + this.lineStyle.offset,
            y2:
                activeObjectCenter.y > objectCenter.y
                    ? activeObjectCenter.y + activeObjectHalfHeight + this.lineStyle.offset
                    : activeObjectCenter.y - activeObjectHalfHeight - this.lineStyle.offset
        });
        this.setObjectPosition(activeObject, snapCenter, activeObjectCenter.y);
    }

    // shift move
    private movementGuides(objectMeasures: FabricObjectMeasures) {
        const objectTop = objectMeasures.centerPoint.y - objectMeasures.halfHeight;
        const objectLeft = objectMeasures.centerPoint.x - objectMeasures.halfWidth;
        const objectWidth = objectMeasures.halfWidth * 2;
        const objectHeight = objectMeasures.halfHeight * 2;

        this.objectHorizontalLines.push(
            {
                x1: 0,
                y1: objectTop,
                x2: this.canvasMeasures.width,
                y2: objectTop
            },
            {
                x1: 0,
                y1: objectTop + objectHeight,
                x2: this.canvasMeasures.width,
                y2: objectTop + objectHeight
            }
        );

        this.objectVerticalLines.push(
            {
                x1: objectLeft,
                y1: 0,
                x2: objectLeft,
                y2: this.canvasMeasures.height
            },
            {
                x1: objectLeft + objectWidth,
                y1: 0,
                x2: objectLeft + objectWidth,
                y2: this.canvasMeasures.height
            }
        );
    }

    /**
     * return true if value2 is within value1 +/- aligningLineMargin
     */
    private isInRange(value1: number, value2: number) {
        return value2 > value1 - this.lineStyle.margin && value2 < value1 + this.lineStyle.margin;
    }

    private setObjectPosition(object: FabricObject, center: number, position: number) {
        object.setPositionByOrigin(new fabric.Point(center, position), 'center', 'center');
    }

    private async afterRender() {
        for (const line of this.objectVerticalLines) {
            this.drawLine(
                line.x1 + 0.5,
                line.y1 > line.y2 ? line.y2 : line.y1,
                line.x2 + 0.5,
                line.y2 > line.y1 ? line.y2 : line.y1
            );
        }

        for (const line of this.objectHorizontalLines) {
            this.drawLine(
                line.x1 > line.x2 ? line.x2 : line.x1,
                line.y1 + 0.5,
                line.x2 > line.x1 ? line.x2 : line.x1,
                line.y2 + 0.5
            );
        }

        if (this.isInVerticalCenter) {
            this.drawLine(
                this.canvasMeasures.centerWidth,
                0,
                this.canvasMeasures.centerWidth,
                this.canvasMeasures.height
            );
        }
        if (this.isInHorizontalCenter) {
            this.drawLine(
                0,
                this.canvasMeasures.centerHeight,
                this.canvasMeasures.width,
                this.canvasMeasures.centerHeight
            );
        }
    }

    private drawLine(x1: number, y1: number, x2: number, y2: number) {
        const zoom = this.canvas.getZoom();
        this.context.save();
        this.context.strokeStyle = this.lineStyle.color;
        this.context.lineWidth = this.lineStyle.width;
        this.context.beginPath();
        this.context.moveTo(x1 * zoom + this.viewportTransform[4], y1 * zoom + this.viewportTransform[5]);
        this.context.lineTo(x2 * zoom + this.viewportTransform[4], y2 * zoom + this.viewportTransform[5]);
        this.context.stroke();
        this.context.restore();
    }

    destroy() {
        this.canvas.off('mouse:down', this.mouseDown);
        this.canvas.off('mouse:up', this.mouseUp);
        this.canvas.off('before:render', this.beforeRender);
        this.canvas.off('object:moving', this.objectMoving);
        this.canvas.off('after:render', this.afterRender);
    }
}
